/**
 * Fleet Management seed data (FLEET-1)
 *
 * All records are stamped with tenantId=kairos-demo via seedLoader so the
 * demo tenant has a realistic owned-fleet footprint to explore. Real
 * tenants provision their own data through the Fleet screens.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const today = new Date();
const daysAhead = (d) => new Date(today.getTime() + d * 86400000).toISOString();
const daysAgo = (d) => new Date(today.getTime() - d * 86400000).toISOString();
const hoursAgo = (h) => new Date(today.getTime() - h * 3600000).toISOString();

const baseMeta = (extra = {}) => ({
  tenantId: DEMO_TENANT_ID,
  createdAt: daysAgo(180),
  createdBy: 'seed',
  updatedAt: daysAgo(1),
  updatedBy: 'seed',
  isDeleted: false,
  softDeleted: false,
  version: 1,
  source: 'fleet_seed',
  ...extra,
});

// ── Vehicles ────────────────────────────────────────────────────────────
export const SEED_FLEET_VEHICLES = [
  {
    id: 'fv-001', vehicleId: 'fv-001', unitNumber: 'WM-1001', type: 'tractor',
    make: 'Freightliner', model: 'Cascadia', year: 2023, vin: '1FUJHHDR4NLXX1001', licensePlate: 'TX 4821AA', state: 'TX',
    status: 'active', currentDriverId: 'fd-001', currentLoadId: 'load-00003',
    odometer: 148320, fuelType: 'Diesel', mpgAverage: 7.2,
    lastInspectionDate: daysAgo(32), nextInspectionDue: daysAhead(58),
    dotNumber: 'DOT-3092841', insuranceExpiry: daysAhead(120),
    gpsDeviceId: 'SAMSARA-A1001', currentLat: 32.7767, currentLng: -96.7970, lastPingAt: hoursAgo(0.2),
    homeTerminal: 'Dallas, TX',
    notes: 'Primary long-haul tractor.',
    ...baseMeta(),
  },
  {
    id: 'fv-002', vehicleId: 'fv-002', unitNumber: 'WM-1002', type: 'tractor',
    make: 'Kenworth', model: 'T680', year: 2022, vin: '1XKDD40X5NJXX1002', licensePlate: 'TX 4822AA', state: 'TX',
    status: 'active', currentDriverId: 'fd-002', currentLoadId: 'load-00014',
    odometer: 212450, fuelType: 'Diesel', mpgAverage: 6.9,
    lastInspectionDate: daysAgo(58), nextInspectionDue: daysAhead(32),
    dotNumber: 'DOT-3092841', insuranceExpiry: daysAhead(120),
    gpsDeviceId: 'SAMSARA-A1002', currentLat: 34.0522, currentLng: -118.2437, lastPingAt: hoursAgo(0.1),
    homeTerminal: 'Dallas, TX',
    notes: '',
    ...baseMeta(),
  },
  {
    id: 'fv-003', vehicleId: 'fv-003', unitNumber: 'WM-1003', type: 'tractor',
    make: 'Peterbilt', model: '579', year: 2024, vin: '1XPBD49X5PD7XX003', licensePlate: 'TX 4823AA', state: 'TX',
    status: 'maintenance', currentDriverId: null, currentLoadId: null,
    odometer: 92105, fuelType: 'Diesel', mpgAverage: 7.4,
    lastInspectionDate: daysAgo(15), nextInspectionDue: daysAhead(75),
    dotNumber: 'DOT-3092841', insuranceExpiry: daysAhead(120),
    gpsDeviceId: 'SAMSARA-A1003', currentLat: 32.7767, currentLng: -96.7970, lastPingAt: hoursAgo(6),
    homeTerminal: 'Dallas, TX',
    notes: 'In shop for brake service.',
    ...baseMeta(),
  },
  {
    id: 'fv-004', vehicleId: 'fv-004', unitNumber: 'WM-1004', type: 'tractor',
    make: 'Volvo', model: 'VNL 860', year: 2021, vin: '4V4NC9EH5MNXX1004', licensePlate: 'TX 4824AA', state: 'TX',
    status: 'active', currentDriverId: 'fd-003', currentLoadId: null,
    odometer: 321780, fuelType: 'Diesel', mpgAverage: 6.7,
    lastInspectionDate: daysAgo(88), nextInspectionDue: daysAhead(2),
    dotNumber: 'DOT-3092841', insuranceExpiry: daysAhead(45),
    gpsDeviceId: 'SAMSARA-A1004', currentLat: 41.8781, currentLng: -87.6298, lastPingAt: hoursAgo(0.4),
    homeTerminal: 'Dallas, TX',
    notes: 'Inspection due soon.',
    ...baseMeta(),
  },
  {
    id: 'fv-005', vehicleId: 'fv-005', unitNumber: 'WM-1005', type: 'tractor',
    make: 'International', model: 'LT625', year: 2020, vin: '3HSDJAPR5LNXX1005', licensePlate: 'TX 4825AA', state: 'TX',
    status: 'out_of_service', currentDriverId: null, currentLoadId: null,
    odometer: 488210, fuelType: 'Diesel', mpgAverage: 6.4,
    lastInspectionDate: daysAgo(135), nextInspectionDue: daysAgo(45),
    dotNumber: 'DOT-3092841', insuranceExpiry: daysAhead(120),
    gpsDeviceId: 'SAMSARA-A1005', currentLat: 32.7767, currentLng: -96.7970, lastPingAt: daysAgo(4),
    homeTerminal: 'Dallas, TX',
    notes: 'Awaiting engine rebuild decision.',
    ...baseMeta(),
  },
  {
    id: 'fv-006', vehicleId: 'fv-006', unitNumber: 'WM-2001', type: 'trailer',
    make: 'Wabash', model: 'DuraPlate 53ft', year: 2022, vin: '1JJV532BXNLXX2001', licensePlate: 'TX 7821TR', state: 'TX',
    status: 'active', currentDriverId: null, currentLoadId: 'load-00003',
    odometer: 0, fuelType: 'n/a', mpgAverage: 0,
    lastInspectionDate: daysAgo(40), nextInspectionDue: daysAhead(50),
    dotNumber: 'DOT-3092841', insuranceExpiry: daysAhead(120),
    gpsDeviceId: 'SAMSARA-T2001', currentLat: 32.7767, currentLng: -96.7970, lastPingAt: hoursAgo(0.5),
    homeTerminal: 'Dallas, TX',
    notes: '53ft dry van',
    ...baseMeta(),
  },
  {
    id: 'fv-007', vehicleId: 'fv-007', unitNumber: 'WM-2002', type: 'reefer_trailer',
    make: 'Utility', model: '3000R Reefer', year: 2023, vin: '1UYVS2534PGXX2002', licensePlate: 'TX 7822TR', state: 'TX',
    status: 'active', currentDriverId: null, currentLoadId: 'load-00014',
    odometer: 0, fuelType: 'n/a', mpgAverage: 0,
    lastInspectionDate: daysAgo(22), nextInspectionDue: daysAhead(68),
    dotNumber: 'DOT-3092841', insuranceExpiry: daysAhead(120),
    gpsDeviceId: 'SAMSARA-T2002', currentLat: 34.0522, currentLng: -118.2437, lastPingAt: hoursAgo(0.3),
    homeTerminal: 'Dallas, TX',
    notes: '53ft reefer, -20°F capable',
    ...baseMeta(),
  },
  {
    id: 'fv-008', vehicleId: 'fv-008', unitNumber: 'WM-1006', type: 'tractor',
    make: 'Freightliner', model: 'Cascadia', year: 2024, vin: '1FUJHHDR4NLXX1006', licensePlate: 'TX 4826AA', state: 'TX',
    status: 'active', currentDriverId: 'fd-004', currentLoadId: null,
    odometer: 48210, fuelType: 'Diesel', mpgAverage: 7.6,
    lastInspectionDate: daysAgo(12), nextInspectionDue: daysAhead(78),
    dotNumber: 'DOT-3092841', insuranceExpiry: daysAhead(120),
    gpsDeviceId: 'SAMSARA-A1006', currentLat: 29.7604, currentLng: -95.3698, lastPingAt: hoursAgo(1),
    homeTerminal: 'Houston, TX',
    notes: '',
    ...baseMeta(),
  },
];

// ── Drivers ─────────────────────────────────────────────────────────────
export const SEED_FLEET_DRIVERS = [
  {
    id: 'fd-001', driverId: 'fd-001', firstName: 'Marcus', lastName: 'Delgado',
    cdlNumber: 'TX-CDL-28491', cdlClass: 'A', cdlState: 'TX', cdlExpiry: daysAhead(420),
    medicalCertExpiry: daysAhead(180), drugTestDate: daysAgo(120),
    status: 'driving', currentVehicleId: 'fv-001', currentLoadId: 'load-00003',
    hoursAvailable: 6.2, hoursUsedToday: 7.8, hoursUsedWeek: 47.5,
    phone: '555-201-0042', email: 'mdelgado@watchmenlogistics.com', homeTerminal: 'Dallas, TX',
    hireDate: daysAgo(820), emergencyContact: 'Elena Delgado · 555-201-9911',
    ...baseMeta(),
  },
  {
    id: 'fd-002', driverId: 'fd-002', firstName: 'Jasmine', lastName: 'Owens',
    cdlNumber: 'TX-CDL-29815', cdlClass: 'A', cdlState: 'TX', cdlExpiry: daysAhead(22),
    medicalCertExpiry: daysAhead(90), drugTestDate: daysAgo(200),
    status: 'driving', currentVehicleId: 'fv-002', currentLoadId: 'load-00014',
    hoursAvailable: 3.4, hoursUsedToday: 10.6, hoursUsedWeek: 58.2,
    phone: '555-201-0058', email: 'jowens@watchmenlogistics.com', homeTerminal: 'Dallas, TX',
    hireDate: daysAgo(1210), emergencyContact: 'Daniel Owens · 555-201-9812',
    ...baseMeta(),
  },
  {
    id: 'fd-003', driverId: 'fd-003', firstName: 'Ty', lastName: 'Nguyen',
    cdlNumber: 'TX-CDL-31009', cdlClass: 'A', cdlState: 'TX', cdlExpiry: daysAhead(800),
    medicalCertExpiry: daysAgo(12), drugTestDate: daysAgo(90),
    status: 'hos_violation', currentVehicleId: 'fv-004', currentLoadId: null,
    hoursAvailable: 0, hoursUsedToday: 11.0, hoursUsedWeek: 60.0,
    phone: '555-201-0071', email: 'tnguyen@watchmenlogistics.com', homeTerminal: 'Dallas, TX',
    hireDate: daysAgo(500), emergencyContact: 'Linh Nguyen · 555-201-7123',
    ...baseMeta(),
  },
  {
    id: 'fd-004', driverId: 'fd-004', firstName: 'Brianna', lastName: 'Walker',
    cdlNumber: 'TX-CDL-30422', cdlClass: 'A', cdlState: 'TX', cdlExpiry: daysAhead(290),
    medicalCertExpiry: daysAhead(365), drugTestDate: daysAgo(45),
    status: 'available', currentVehicleId: 'fv-008', currentLoadId: null,
    hoursAvailable: 11.0, hoursUsedToday: 0, hoursUsedWeek: 22.0,
    phone: '555-201-0088', email: 'bwalker@watchmenlogistics.com', homeTerminal: 'Houston, TX',
    hireDate: daysAgo(300), emergencyContact: 'Robert Walker · 555-201-3322',
    ...baseMeta(),
  },
  {
    id: 'fd-005', driverId: 'fd-005', firstName: 'Hector', lastName: 'Ramos',
    cdlNumber: 'TX-CDL-30851', cdlClass: 'A', cdlState: 'TX', cdlExpiry: daysAhead(610),
    medicalCertExpiry: daysAhead(240), drugTestDate: daysAgo(70),
    status: 'off_duty', currentVehicleId: null, currentLoadId: null,
    hoursAvailable: 11.0, hoursUsedToday: 0, hoursUsedWeek: 38.0,
    phone: '555-201-0099', email: 'hramos@watchmenlogistics.com', homeTerminal: 'Dallas, TX',
    hireDate: daysAgo(200), emergencyContact: 'Maria Ramos · 555-201-4410',
    ...baseMeta(),
  },
  {
    id: 'fd-006', driverId: 'fd-006', firstName: 'Alicia', lastName: 'Park',
    cdlNumber: 'TX-CDL-30999', cdlClass: 'A', cdlState: 'TX', cdlExpiry: daysAhead(180),
    medicalCertExpiry: daysAhead(45), drugTestDate: daysAgo(30),
    status: 'available', currentVehicleId: null, currentLoadId: null,
    hoursAvailable: 11.0, hoursUsedToday: 0, hoursUsedWeek: 0,
    phone: '555-201-0101', email: 'apark@watchmenlogistics.com', homeTerminal: 'Dallas, TX',
    hireDate: daysAgo(90), emergencyContact: 'Jin Park · 555-201-7788',
    ...baseMeta(),
  },
];

// ── Maintenance ─────────────────────────────────────────────────────────
export const SEED_FLEET_MAINTENANCE = [
  {
    id: 'fm-001', maintenanceId: 'fm-001', vehicleId: 'fv-003',
    type: 'repair', status: 'in_progress',
    description: 'Brake pad replacement — all four axles',
    vendor: 'Dallas Freightliner Service', cost: 2850,
    scheduledDate: daysAgo(1), completedDate: null,
    odometer: 92105, partsReplaced: ['Brake pads (x8)', 'Rotor (x2)'], laborHours: 8,
    nextServiceDue: daysAhead(365), nextServiceMiles: 200000,
    ...baseMeta(),
  },
  {
    id: 'fm-002', maintenanceId: 'fm-002', vehicleId: 'fv-004',
    type: 'inspection', status: 'scheduled',
    description: 'DOT annual inspection',
    vendor: 'Certified DOT Inspection', cost: 325,
    scheduledDate: daysAhead(2), completedDate: null,
    odometer: 321780, partsReplaced: [], laborHours: 2,
    nextServiceDue: daysAhead(367), nextServiceMiles: 0,
    ...baseMeta(),
  },
  {
    id: 'fm-003', maintenanceId: 'fm-003', vehicleId: 'fv-001',
    type: 'scheduled', status: 'scheduled',
    description: 'PM-A — oil change, filters, lube',
    vendor: 'Fleet Services Dallas', cost: 580,
    scheduledDate: daysAhead(8), completedDate: null,
    odometer: 148320, partsReplaced: [], laborHours: 3,
    nextServiceDue: daysAhead(98), nextServiceMiles: 173320,
    ...baseMeta(),
  },
  {
    id: 'fm-004', maintenanceId: 'fm-004', vehicleId: 'fv-005',
    type: 'repair', status: 'scheduled',
    description: 'Engine inspection — coolant loss diagnosis',
    vendor: 'Dallas International Truck', cost: 0,
    scheduledDate: daysAhead(4), completedDate: null,
    odometer: 488210, partsReplaced: [], laborHours: 0,
    nextServiceDue: null, nextServiceMiles: null,
    ...baseMeta(),
  },
  {
    id: 'fm-005', maintenanceId: 'fm-005', vehicleId: 'fv-002',
    type: 'scheduled', status: 'complete',
    description: 'PM-B — 100K service: all fluids, belts, tires rotated',
    vendor: 'Fleet Services Dallas', cost: 1650,
    scheduledDate: daysAgo(28), completedDate: daysAgo(27),
    odometer: 200000, partsReplaced: ['Engine oil', 'Transmission fluid', 'Air filter', 'Fuel filters (x2)'], laborHours: 6,
    nextServiceDue: daysAhead(62), nextServiceMiles: 225000,
    ...baseMeta(),
  },
  {
    id: 'fm-006', maintenanceId: 'fm-006', vehicleId: 'fv-007',
    type: 'inspection', status: 'complete',
    description: 'Reefer unit refrigerant certification',
    vendor: 'Thermo King Authorized', cost: 420,
    scheduledDate: daysAgo(22), completedDate: daysAgo(22),
    odometer: 0, partsReplaced: [], laborHours: 2,
    nextServiceDue: daysAhead(343), nextServiceMiles: 0,
    ...baseMeta(),
  },
];

// ── Compliance ──────────────────────────────────────────────────────────
export const SEED_FLEET_COMPLIANCE = [
  { id: 'fc-001', complianceId: 'fc-001', entityType: 'driver', entityId: 'fd-002', documentType: 'CDL', issueDate: daysAgo(1460), expiryDate: daysAhead(22), status: 'expiring_soon', documentUrl: '', notes: 'Renewal scheduled', reminderSent: true, ...baseMeta() },
  { id: 'fc-002', complianceId: 'fc-002', entityType: 'driver', entityId: 'fd-003', documentType: 'Medical Cert', issueDate: daysAgo(420), expiryDate: daysAgo(12), status: 'expired', documentUrl: '', notes: 'URGENT — driver HOS-blocked until renewed', reminderSent: true, ...baseMeta() },
  { id: 'fc-003', complianceId: 'fc-003', entityType: 'driver', entityId: 'fd-006', documentType: 'Medical Cert', issueDate: daysAgo(320), expiryDate: daysAhead(45), status: 'expiring_soon', documentUrl: '', notes: '', reminderSent: false, ...baseMeta() },
  { id: 'fc-004', complianceId: 'fc-004', entityType: 'vehicle', entityId: 'fv-004', documentType: 'Annual Inspection', issueDate: daysAgo(350), expiryDate: daysAhead(2), status: 'expiring_soon', documentUrl: '', notes: 'Inspection booked', reminderSent: true, ...baseMeta() },
  { id: 'fc-005', complianceId: 'fc-005', entityType: 'vehicle', entityId: 'fv-005', documentType: 'Annual Inspection', issueDate: daysAgo(410), expiryDate: daysAgo(45), status: 'expired', documentUrl: '', notes: 'Vehicle OOS', reminderSent: true, ...baseMeta() },
  { id: 'fc-006', complianceId: 'fc-006', entityType: 'vehicle', entityId: 'fv-004', documentType: 'Insurance', issueDate: daysAgo(320), expiryDate: daysAhead(45), status: 'expiring_soon', documentUrl: '', notes: '', reminderSent: false, ...baseMeta() },
  { id: 'fc-007', complianceId: 'fc-007', entityType: 'vehicle', entityId: 'fv-001', documentType: 'IFTA Decal', issueDate: daysAgo(30), expiryDate: daysAhead(335), status: 'valid', documentUrl: '', notes: '', reminderSent: false, ...baseMeta() },
  { id: 'fc-008', complianceId: 'fc-008', entityType: 'vehicle', entityId: 'fv-002', documentType: 'IFTA Decal', issueDate: daysAgo(30), expiryDate: daysAhead(335), status: 'valid', documentUrl: '', notes: '', reminderSent: false, ...baseMeta() },
  { id: 'fc-009', complianceId: 'fc-009', entityType: 'driver', entityId: 'fd-001', documentType: 'CDL', issueDate: daysAgo(600), expiryDate: daysAhead(420), status: 'valid', documentUrl: '', notes: '', reminderSent: false, ...baseMeta() },
  { id: 'fc-010', complianceId: 'fc-010', entityType: 'driver', entityId: 'fd-004', documentType: 'Drug Test', issueDate: daysAgo(45), expiryDate: daysAhead(320), status: 'valid', documentUrl: '', notes: '', reminderSent: false, ...baseMeta() },
];

export const FLEET_SEED_SUMMARY = {
  fleet_vehicles: SEED_FLEET_VEHICLES.length,
  fleet_drivers: SEED_FLEET_DRIVERS.length,
  fleet_maintenance: SEED_FLEET_MAINTENANCE.length,
  fleet_compliance: SEED_FLEET_COMPLIANCE.length,
};
